import React, { useEffect, useState } from 'react';
import {
    Container, Tabs, Tab, Form, Button, Alert,
    Card, Row, Col, Spinner, Modal,
    OverlayTrigger, Tooltip
} from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { withRouter } from 'react-router-dom';
import firebase from '../tools/firebase';
import logo from '../assets/LaunchScreen@2x.png';
import PlacesAutocomplete from './PlacesAutocomplete';
import Footer from '../components/Footer';
import style from '../style/admin.module.css';

// ─── Truck Info ───────────────────────────────────────────────────────────────

const TruckInfoSection = ({ truckId, initialData }) => {
    const social = initialData.socialmedia || [];
    const findLink = key => (social.find(s => s.key === key) || {}).link || '';

    const [form, setForm] = useState({
        title:       initialData.title       || '',
        description: initialData.description || '',
        image:       initialData.image        || '',
        facebook:    findLink('facebook'),
        instagram:   findLink('instagram'),
        twitter:     findLink('twitter'),
        website:     findLink('website'),
    });
    const [saving, setSaving]   = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError]     = useState('');

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSuccess(false);
        setError('');
        try {
            const socialmedia = ['facebook', 'instagram', 'twitter', 'website']
                .map(k => ({ key: k, link: form[k] || '', title: k.charAt(0).toUpperCase() + k.slice(1) }));
            await firebase.firestore().collection('trucks').doc(truckId).update({
                title:       form.title,
                description: form.description,
                image:       form.image,
                socialmedia,
            });
            setSuccess(true);
        } catch {
            setError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form onSubmit={handleSave} className={style.sectionForm}>
            {success && <Alert variant="success" onClose={() => setSuccess(false)} dismissible>Saved!</Alert>}
            {error   && <Alert variant="danger">{error}</Alert>}

            <Form.Group>
                <Form.Label>Truck Name</Form.Label>
                <Form.Control
                    value={form.title}
                    onChange={e => set('title', e.target.value)}
                    required
                />
            </Form.Group>

            <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.description}
                    onChange={e => set('description', e.target.value)}
                />
            </Form.Group>

            <Form.Group>
                <Form.Label>Hero Image URL</Form.Label>
                <Form.Control
                    value={form.image}
                    onChange={e => set('image', e.target.value)}
                    placeholder="https://..."
                />
            </Form.Group>

            <h6 className={style.subheading}>Social Media</h6>
            {['facebook', 'instagram', 'twitter', 'website'].map(platform => (
                <Form.Group key={platform}>
                    <Form.Label className={style.capitalize}>{platform}</Form.Label>
                    <Form.Control
                        value={form[platform]}
                        onChange={e => set(platform, e.target.value)}
                        placeholder={`https://${platform}.com/yourpage`}
                    />
                </Form.Group>
            ))}

            <Button type="submit" className={style.primaryBtn} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
            </Button>
        </Form>
    );
};

// ─── Menu ─────────────────────────────────────────────────────────────────────

const MENU_CATEGORIES = ['Entrees', 'Desserts', 'Drinks', 'Extras'];

const MenuItemModal = ({ show, onHide, onSave, item }) => {
    const [form, setForm] = useState({ name: '', price: '', image: '', active: true, gtamount: '', description: '' });

    useEffect(() => {
        setForm({
            name:        item?.name        || '',
            price:       item?.price       || '',
            image:       item?.image       || '',
            active:      item?.active !== false,
            gtamount:    item?.gtamount    || '',
            description: item?.description || '',
        });
    }, [item, show]);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{item?.id ? 'Edit Item' : 'Add Item'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                        value={form.name}
                        onChange={e => set('name', e.target.value)}
                        required
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>Price</Form.Label>
                    <Form.Control
                        value={form.price}
                        onChange={e => set('price', e.target.value)}
                        placeholder="e.g. 8.99"
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>Image URL</Form.Label>
                    <Form.Control
                        value={form.image}
                        onChange={e => set('image', e.target.value)}
                        placeholder="https://..."
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={form.description}
                        onChange={e => set('description', e.target.value)}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Label>Amount Unit</Form.Label>
                    <Form.Control
                        as="select"
                        value={form.gtamount}
                        onChange={e => set('gtamount', e.target.value)}
                    >
                        <option value="">—</option>
                        <option value="each">each</option>
                        <option value="slices">slices</option>
                        <option value="piece">piece</option>
                    </Form.Control>
                </Form.Group>
                <Form.Group className={style.activeToggleGroup}>
                    <div className={style.activeToggleRow}>
                        <div>
                            <div className={style.activeToggleLabel}>Visibility</div>
                            <div className={style.activeToggleHint}>
                                {form.active ? 'Visible to customers' : 'Hidden from customers'}
                            </div>
                        </div>
                        <Form.Check
                            type="switch"
                            id="menu-item-active"
                            checked={form.active}
                            onChange={e => set('active', e.target.checked)}
                            className={style.activeSwitch}
                        />
                    </div>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button className={style.primaryBtn} onClick={() => onSave(form)}>
                    {item?.id ? 'Save Changes' : 'Add Item'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const MenuSection = ({ truckId }) => {
    const [activeCategory, setActiveCategory] = useState('Entrees');
    const [items, setItems]       = useState({});
    const [loading, setLoading]   = useState(false);
    const [modalOpen, setModal]   = useState(false);
    const [editingItem, setEditing] = useState(null);
    const [refreshKey, setRefresh]  = useState(0);

    useEffect(() => {
        setLoading(true);
        const db = firebase.firestore();
        Promise.all(
            MENU_CATEGORIES.map(cat =>
                db.collection('trucks').doc(truckId).collection(cat).get()
                    .then(snap => {
                        const list = [];
                        snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
                        return { cat, list };
                    })
            )
        )
            .then(results => {
                const map = {};
                results.forEach(({ cat, list }) => { map[cat] = list; });
                setItems(map);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [truckId, refreshKey]);

    const handleSave = async (form) => {
        const db = firebase.firestore();
        const colRef = db.collection('trucks').doc(truckId).collection(activeCategory);
        const data = {
            name:        form.name        || '',
            price:       form.price       || '',
            image:       form.image       || '',
            active:      form.active !== false,
            gtamount:    form.gtamount    || '',
            description: form.description || '',
        };
        if (editingItem?.id) {
            await colRef.doc(editingItem.id).update(data);
        } else {
            await colRef.add(data);
        }
        setModal(false);
        setRefresh(k => k + 1);
    };

    const handleToggleActive = async (item) => {
        await firebase.firestore()
            .collection('trucks').doc(truckId)
            .collection(activeCategory).doc(item.id)
            .update({ active: !item.active });
        setRefresh(k => k + 1);
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete "${item.name}"?`)) return;
        await firebase.firestore()
            .collection('trucks').doc(truckId)
            .collection(activeCategory).doc(item.id)
            .delete();
        setRefresh(k => k + 1);
    };

    return (
        <div>
            <Tabs
                activeKey={activeCategory}
                onSelect={cat => setActiveCategory(cat)}
                className={style.innerTabs}
            >
                {MENU_CATEGORIES.map(cat => (
                    <Tab key={cat} eventKey={cat} title={cat}>
                        <div className={style.menuTabContent}>
                            <Button
                                className={style.primaryBtn}
                                size="sm"
                                onClick={() => { setEditing(null); setModal(true); }}
                            >
                                + Add Item
                            </Button>

                            {loading ? (
                                <div className={style.centered}>
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <Row className="mt-3">
                                    {(items[cat] || []).length === 0 ? (
                                        <Col>
                                            <p className={style.emptyMsg}>No items yet.</p>
                                        </Col>
                                    ) : (
                                        (items[cat] || []).map(item => (
                                            <Col key={item.id} xs={12} sm={6} md={4} className="mb-3">
                                                <Card className={`${style.menuCard} ${item.active === false ? style.menuCardInactive : ''}`}>
                                                    {item.image && (
                                                        <div className={style.menuCardImgWrap}>
                                                            <Card.Img
                                                                variant="top"
                                                                src={item.image}
                                                                className={style.menuCardImg}
                                                            />
                                                            {item.active === false && (
                                                                <div className={style.inactiveOverlay}>Inactive</div>
                                                            )}
                                                        </div>
                                                    )}
                                                    <Card.Body className={style.menuCardBody}>
                                                        <div className={style.menuCardHeader}>
                                                            <Card.Title className={style.menuCardTitle}>
                                                                {item.name}
                                                            </Card.Title>
                                                            <span className={item.active === false ? style.statusBadgeInactive : style.statusBadgeActive}>
                                                                {item.active === false ? 'Inactive' : 'Active'}
                                                            </span>
                                                        </div>
                                                        {item.description && (
                                                            <p className={style.menuCardDescription}>{item.description}</p>
                                                        )}
                                                        {item.price != null && item.price !== '' && (
                                                            <span className={style.menuCardPrice}>
                                                                {parseFloat(item.price) === 0 ? 'Free' : `$${item.price}`}
                                                                {item.gtamount ? ` / ${item.gtamount}` : ''}
                                                            </span>
                                                        )}
                                                        <div className={style.cardActions}>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                onClick={() => { setEditing(item); setModal(true); }}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant={item.active === false ? 'outline-success' : 'outline-warning'}
                                                                onClick={() => handleToggleActive(item)}
                                                            >
                                                                {item.active === false ? 'Activate' : 'Deactivate'}
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleDelete(item)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))
                                    )}
                                </Row>
                            )}
                        </div>
                    </Tab>
                ))}
            </Tabs>

            <MenuItemModal
                show={modalOpen}
                onHide={() => setModal(false)}
                onSave={handleSave}
                item={editingItem}
            />
        </div>
    );
};

// ─── Schedule ─────────────────────────────────────────────────────────────────

const tsToInput = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatTs = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const ScheduleModal = ({ show, onHide, onSave, entry }) => {
    const [form, setForm] = useState({ address: '', timeFrom: '', timeTo: '' });

    useEffect(() => {
        setForm({
            address:  entry?.address  || '',
            timeFrom: tsToInput(entry?.timeFrom),
            timeTo:   tsToInput(entry?.timeTo),
        });
    }, [entry, show]);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{entry?.id ? 'Edit Schedule Entry' : 'Add Schedule Entry'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label>Address</Form.Label>
                    <PlacesAutocomplete
                        value={form.address}
                        onChange={val => set('address', val)}
                        placeholder="Address or location name"
                        className={`form-control ${style.placesInput}`}
                    />
                </Form.Group>
                <Row>
                    <Col>
                        <Form.Group>
                            <Form.Label>From</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={form.timeFrom}
                                onChange={e => set('timeFrom', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group>
                            <Form.Label>To</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={form.timeTo}
                                onChange={e => set('timeTo', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button className={style.primaryBtn} onClick={() => onSave(form)}>
                    {entry?.id ? 'Save Changes' : 'Add Entry'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const ScheduleSection = ({ truckId }) => {
    const [entries, setEntries]     = useState([]);
    const [loading, setLoading]     = useState(true);
    const [saveError, setSaveError] = useState('');
    const [loadError, setLoadError] = useState('');
    const [modalOpen, setModal]     = useState(false);
    const [editingEntry, setEditing] = useState(null);
    const [refreshKey, setRefresh]   = useState(0);

    useEffect(() => {
        setLoading(true);
        firebase.firestore()
            .collection('schedule')
            .where('truckID', '==', truckId)
            .get()
            .then(snap => {
                const list = [];
                snap.forEach(doc => { const d = doc.data(); if (d.address !== '') list.push({ id: doc.id, ...d }); });
                list.sort((a, b) => {
                    const ta = a.timeFrom?.toDate?.()?.getTime() || 0;
                    const tb = b.timeFrom?.toDate?.()?.getTime() || 0;
                    return ta - tb;
                });
                setEntries(list);
            })
            .catch(err => setLoadError(err.message || 'Failed to load schedule.'))
            .finally(() => setLoading(false));
    }, [truckId, refreshKey]);

    const handleSave = async (form) => {
        setSaveError('');
        try {
            const db = firebase.firestore();
            const colRef = db.collection('schedule');
            const data = {
                truckID:  truckId,
                address:  form.address  || '',
                timeFrom: form.timeFrom ? firebase.firestore.Timestamp.fromDate(new Date(form.timeFrom)) : null,
                timeTo:   form.timeTo   ? firebase.firestore.Timestamp.fromDate(new Date(form.timeTo))   : null,
            };
            if (editingEntry?.id) {
                await colRef.doc(editingEntry.id).update(data);
            } else {
                await colRef.add(data);
            }
            setModal(false);
            setRefresh(k => k + 1);
        } catch (err) {
            setSaveError(err.message || 'Failed to save. Please try again.');
        }
    };

    const handleDelete = async (entry) => {
        if (!window.confirm('Delete this schedule entry?')) return;
        await firebase.firestore()
            .collection('schedule').doc(entry.id)
            .delete();
        setRefresh(k => k + 1);
    };

    return (
        <div className={style.sectionForm}>
            <Button
                className={style.primaryBtn}
                size="sm"
                onClick={() => { setEditing(null); setSaveError(''); setModal(true); }}
            >
                + Add Schedule Entry
            </Button>

            {loadError && <Alert variant="danger" className="mt-2">{loadError}</Alert>}
            {saveError && <Alert variant="danger" className="mt-2">{saveError}</Alert>}

            {loading ? (
                <div className={style.centered}>
                    <Spinner animation="border" size="sm" />
                </div>
            ) : entries.length === 0 ? (
                <p className={`${style.emptyMsg} mt-3`}>No schedule entries yet.</p>
            ) : (
                <div className={style.scheduleList}>
                    {entries.map(entry => (
                        <div key={entry.id} className={style.scheduleCard}>
                            <div className={style.scheduleCardMain}>
                                {entry.address && (
                                    <div className={style.scheduleLocation}>{entry.address}</div>
                                )}
                                {entry.timeFrom && (
                                    <div className={style.scheduleTime}>{formatTs(entry.timeFrom)}</div>
                                )}
                                {entry.timeTo && (
                                    <div className={style.scheduleTime}>– {formatTs(entry.timeTo)}</div>
                                )}
                            </div>
                            <div className={style.cardActions}>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => { setEditing(entry); setModal(true); }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(entry)}
                                >
                                    Delete
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ScheduleModal
                show={modalOpen}
                onHide={() => setModal(false)}
                onSave={handleSave}
                entry={editingEntry}
            />
        </div>
    );
};

// ─── Awards ──────────────────────────────────────────────────────────────────


const AwardsSection = ({ truckId }) => {
    const [awards, setAwards]   = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        firebase.firestore()
            .collection('trucks').doc(truckId).collection('awards')
            .get()
            .then(snap => {
                const list = [];
                snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
                list.sort((a, b) => (b.year || '').localeCompare(a.year || ''));
                setAwards(list);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [truckId]);

    if (loading) {
        return (
            <div className={style.centered}>
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (awards.length === 0) {
        return <p className={`${style.emptyMsg} mt-2`}>No awards yet.</p>;
    }

    return (
        <div className={style.awardsList}>
            {awards.map(award => (
                <div key={award.id} className={style.awardCard}>
                    <div className={style.awardCardIcon}>&#127942;</div>
                    <div className={style.awardCardBody}>
                        <div className={style.awardCardTitle}>{award.title}</div>
                        {(award.issuer || award.year) && (
                            <div className={style.awardCardMeta}>
                                {[award.issuer, award.year].filter(Boolean).join(' · ')}
                            </div>
                        )}
                        {award.description && (
                            <div className={style.awardCardDescription}>{award.description}</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

// ─── Comments ────────────────────────────────────────────────────────────────

const CommentsSection = ({ truckId }) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading]   = useState(true);

    useEffect(() => {
        firebase.firestore()
            .collection('comments')
            .where('truckID', '==', truckId)
            .get()
            .then(snap => {
                const list = [];
                snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
                setComments(list);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [truckId]);

    if (loading) {
        return (
            <div className={style.centered}>
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (comments.length === 0) {
        return <p className={`${style.emptyMsg} mt-2`}>No reviews yet.</p>;
    }

    return (
        <div className={style.commentsFeed}>
            {comments.map(c => (
                <div key={c.id} className={style.commentFeedCard}>
                    <div className={style.commentFeedHeader}>
                        <span className={style.commentFeedUser}>{c.userName}</span>
                        {c.dateofcomment && (
                            <span className={style.commentFeedDate}>{c.dateofcomment}</span>
                        )}
                    </div>
                    <p className={style.commentFeedText}>{c.comment}</p>
                </div>
            ))}
        </div>
    );
};

// ─── Analytics helpers ───────────────────────────────────────────────────────

const InfoTip = ({ text }) => (
    <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{text}</Tooltip>}
    >
        <FaInfoCircle className={style.infoIcon} />
    </OverlayTrigger>
);

// ─── Analytics ───────────────────────────────────────────────────────────────

const AnalyticsSection = ({ truckId }) => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats]     = useState(null);

    useEffect(() => {
        const db  = firebase.firestore();
        const now = new Date();

        Promise.all([
            db.collection('comments').where('truckID', '==', truckId).get(),
            db.collection('schedule').where('truckID', '==', truckId).get(),
            db.collection('favorites').where('truckID', '==', truckId).get(),
            db.collection('trucks').doc(truckId).collection('awards').get(),
            Promise.all(
                MENU_CATEGORIES.map(cat =>
                    db.collection('trucks').doc(truckId).collection(cat).get()
                        .then(snap => {
                            const list = [];
                            snap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
                            return { cat, list };
                        })
                )
            ),
        ])
        .then(([commentsSnap, scheduleSnap, favoritesSnap, awardsSnap, menuResults]) => {
            const comments = [];
            commentsSnap.forEach(doc => comments.push(doc.data()));

            const scheduleEntries = [];
            scheduleSnap.forEach(doc => {
                const d = doc.data();
                if (d.address !== '') scheduleEntries.push({ id: doc.id, ...d });
            });

            let totalHours    = 0;
            let upcomingCount = 0;
            const locationCount = {};

            scheduleEntries.forEach(entry => {
                if (entry.timeFrom && entry.timeTo) {
                    const from = entry.timeFrom.toDate ? entry.timeFrom.toDate() : new Date(entry.timeFrom);
                    const to   = entry.timeTo.toDate   ? entry.timeTo.toDate()   : new Date(entry.timeTo);
                    const hrs  = (to - from) / 3600000;
                    if (hrs > 0) totalHours += hrs;
                }
                if (entry.address) {
                    locationCount[entry.address] = (locationCount[entry.address] || 0) + 1;
                }
                if (entry.timeFrom) {
                    const from = entry.timeFrom.toDate ? entry.timeFrom.toDate() : new Date(entry.timeFrom);
                    if (from > now) upcomingCount++;
                }
            });

            const topLocation = Object.entries(locationCount).sort((a, b) => b[1] - a[1])[0];

            const menuMap          = {};
            menuResults.forEach(({ cat, list }) => { menuMap[cat] = list; });
            const totalMenuItems   = menuResults.reduce((s, { list }) => s + list.length, 0);
            const activeMenuItems  = menuResults.reduce((s, { list }) => s + list.filter(i => i.active !== false).length, 0);

            const avgHours = scheduleEntries.length
                ? Math.round((totalHours / scheduleEntries.length) * 10) / 10
                : 0;

            const awards = [];
            awardsSnap.forEach(doc => awards.push({ id: doc.id, ...doc.data() }));
            awards.sort((a, b) => (b.year || '').localeCompare(a.year || ''));

            setStats({
                totalFavorites:    favoritesSnap.size,
                totalComments:     comments.length,
                awards,
                totalEvents:       scheduleEntries.length,
                totalHours:        Math.round(totalHours * 10) / 10,
                avgHours,
                upcomingEvents:    upcomingCount,
                topLocation:       topLocation ? topLocation[0] : null,
                topLocationCount:  topLocation ? topLocation[1] : 0,
                totalMenuItems,
                activeMenuItems,
                menuByCategory:    menuMap,
            });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, [truckId]);

    if (loading) {
        return (
            <div className={style.centered}>
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (!stats) return <Alert variant="danger">Failed to load analytics.</Alert>;

    return (
        <div>
            <Row className="mb-3">
                <Col xs={6} sm={3} className="mb-3">
                    <div className={style.statCard}>
                        <div className={style.statCardHeader}>
                            <InfoTip text="Number of unique devices that have favorited this truck on its public page." />
                        </div>
                        <div className={style.statValue}>{stats.totalFavorites}</div>
                        <div className={style.statLabel}>Favorites</div>
                    </div>
                </Col>
                <Col xs={6} sm={3} className="mb-3">
                    <div className={style.statCard}>
                        <div className={style.statCardHeader}>
                            <InfoTip text="Total number of customer reviews posted on this truck's public page." />
                        </div>
                        <div className={style.statValue}>{stats.totalComments}</div>
                        <div className={style.statLabel}>Comments</div>
                    </div>
                </Col>
                <Col xs={6} sm={3} className="mb-3">
                    <div className={style.statCard}>
                        <div className={style.statCardHeader}>
                            <InfoTip text="Sum of all scheduled event durations — how many hours this truck has been available in total." />
                        </div>
                        <div className={style.statValue}>{stats.totalHours}h</div>
                        <div className={style.statLabel}>Hours Scheduled</div>
                    </div>
                </Col>
                <Col xs={6} sm={3} className="mb-3">
                    <div className={style.statCard}>
                        <div className={style.statCardHeader}>
                            <InfoTip text="Total items across all menu categories, including inactive ones." />
                        </div>
                        <div className={style.statValue}>{stats.totalMenuItems}</div>
                        <div className={style.statLabel}>Menu Items</div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col xs={12} md={6} className="mb-3">
                    <div className={style.analyticsCard}>
                        <div className={style.analyticsCardTitle}>
                            Most Visited Location
                            <InfoTip text="The address that appears most frequently across all your schedule entries." />
                        </div>
                        {stats.topLocation ? (
                            <>
                                <div className={style.analyticsHighlight}>{stats.topLocation}</div>
                                <div className={style.analyticsMeta}>
                                    Scheduled {stats.topLocationCount} time{stats.topLocationCount !== 1 ? 's' : ''}
                                </div>
                            </>
                        ) : (
                            <p className={style.emptyMsg}>No schedule data yet.</p>
                        )}
                    </div>
                </Col>

                <Col xs={12} md={6} className="mb-3">
                    <div className={style.analyticsCard}>
                        <div className={style.analyticsCardTitle}>
                            Schedule Summary
                            <InfoTip text="A breakdown of your events: how many are still upcoming, how many have passed, and the average length of a session." />
                        </div>
                        <div className={style.analyticsSummaryRow}>
                            <span className={style.analyticsSummaryLabel}>Upcoming events</span>
                            <span className={style.analyticsSummaryValue}>{stats.upcomingEvents}</span>
                        </div>
                        <div className={style.analyticsSummaryRow}>
                            <span className={style.analyticsSummaryLabel}>Past events</span>
                            <span className={style.analyticsSummaryValue}>{stats.totalEvents - stats.upcomingEvents}</span>
                        </div>
                        <div className={style.analyticsSummaryRow}>
                            <span className={style.analyticsSummaryLabel}>Avg. session length</span>
                            <span className={style.analyticsSummaryValue}>{stats.avgHours}h</span>
                        </div>
                        <div className={style.analyticsSummaryRow}>
                            <span className={style.analyticsSummaryLabel}>Total hours available</span>
                            <span className={style.analyticsSummaryValue}>{stats.totalHours}h</span>
                        </div>
                    </div>
                </Col>

                <Col xs={12} md={6} className="mb-3">
                    <div className={style.analyticsCard}>
                        <div className={style.analyticsCardTitle}>
                            Menu Breakdown
                            <InfoTip text="Item counts per category. 'Active' items are currently visible to customers on your public page." />
                        </div>
                        {MENU_CATEGORIES.map(cat => (
                            <div key={cat} className={style.analyticsSummaryRow}>
                                <span className={style.analyticsSummaryLabel}>{cat}</span>
                                <span className={style.analyticsSummaryValue}>
                                    {(stats.menuByCategory[cat] || []).length} items
                                    <span className={style.menuActiveChip}>
                                        {(stats.menuByCategory[cat] || []).filter(i => i.active !== false).length} active
                                    </span>
                                </span>
                            </div>
                        ))}
                        <div className={`${style.analyticsMeta} mt-2`}>
                            {stats.activeMenuItems} of {stats.totalMenuItems} items visible to customers
                        </div>
                    </div>
                </Col>

                {stats.awards.length > 0 && (
                    <Col xs={12} className="mb-3">
                        <div className={style.analyticsCard}>
                            <div className={style.analyticsCardTitle}>
                                Awards &amp; Recognition
                                <InfoTip text="Awards this truck has received, managed from the Awards tab." />
                            </div>
                            <div className={style.analyticsAwardsList}>
                                {stats.awards.map(award => (
                                    <div key={award.id} className={style.analyticsAwardItem}>
                                        <span className={style.analyticsAwardIcon}>&#127942;</span>
                                        <div>
                                            <div className={style.analyticsAwardTitle}>{award.title}</div>
                                            {(award.issuer || award.year) && (
                                                <div className={style.analyticsMeta}>
                                                    {[award.issuer, award.year].filter(Boolean).join(' · ')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Col>
                )}
            </Row>

        </div>
    );
};

// ─── Main TruckAdmin Component ────────────────────────────────────────────────

const TruckAdmin = ({ match, history }) => {
    const truckId = match.params.truckId;
    const [truckData, setTruckData] = useState(null);
    const [loading, setLoading]     = useState(true);

    useEffect(() => {
        firebase.firestore().collection('trucks').doc(truckId).get()
            .then(doc => { if (doc.exists) setTruckData(doc.data()); })
            .finally(() => setLoading(false));
    }, [truckId]);

    const handleSignOut = () => {
        firebase.auth().signOut().then(() => history.push('/admin'));
    };

    if (loading) {
        return (
            <div className={style.centered} style={{ marginTop: '3rem' }}>
                <Spinner animation="border" />
            </div>
        );
    }

    if (!truckData) {
        return (
            <Container className="mt-4">
                <p>Truck not found.</p>
                <Button variant="link" onClick={() => history.push('/admin/dashboard')}>
                    ← Back to Dashboard
                </Button>
            </Container>
        );
    }

    return (
        <div className={style.adminPage}>
            <div
                className={style.truckAdminHero}
                style={truckData.image ? { backgroundImage: `url(${truckData.image})` } : {}}
            >
                <div className={style.truckAdminHeroOverlay} />
                <Container>
                    <div className={style.truckAdminHeroInner}>
                        <div className={style.truckAdminHeroLeft}>
                            <button
                                className={style.backLink}
                                onClick={() => history.push('/admin/dashboard')}
                            >
                                ← My Trucks
                            </button>
                            <div className={style.truckAdminHeroTitle}>
                                <img src={logo} alt="Grub Trucks" className={style.truckAdminLogo} />
                                <div>
                                    <h2 className={style.topBarTitle}>{truckData.title}</h2>
                                    <a
                                        href={`/trucks/${truckId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={style.viewProfileLink}
                                    >
                                        View Public Profile ↗
                                    </a>
                                </div>
                            </div>
                        </div>
                        <Button className={style.signOutBtn} onClick={handleSignOut}>
                            Sign Out
                        </Button>
                    </div>
                </Container>
            </div>

            <Container className={style.pageContent}>
                <Tabs defaultActiveKey="info" className={style.mainTabs}>
                    <Tab eventKey="info" title="Truck Info">
                        <div className={style.tabPanel}>
                            <TruckInfoSection truckId={truckId} initialData={truckData} />
                        </div>
                    </Tab>
                    <Tab eventKey="menu" title="Menu">
                        <div className={style.tabPanel}>
                            <MenuSection truckId={truckId} />
                        </div>
                    </Tab>
                    <Tab eventKey="schedule" title="Schedule">
                        <div className={style.tabPanel}>
                            <ScheduleSection truckId={truckId} />
                        </div>
                    </Tab>
                    <Tab eventKey="awards" title="Awards">
                        <div className={style.tabPanel}>
                            <AwardsSection truckId={truckId} />
                        </div>
                    </Tab>
                    <Tab eventKey="reviews" title="Reviews">
                        <div className={style.tabPanel}>
                            <CommentsSection truckId={truckId} />
                        </div>
                    </Tab>
                    <Tab eventKey="analytics" title="Analytics">
                        <div className={style.tabPanel}>
                            <AnalyticsSection truckId={truckId} />
                        </div>
                    </Tab>
                </Tabs>
            </Container>
            <Footer />
        </div>
    );
};

export default withRouter(TruckAdmin);
